import { useEffect, useRef, useState } from "react";
import { geocodeAddress } from "../../../shared/geo";
import { leafletPinPickerHtml } from "../../../shared/places";

export type PickedPoint = { latitude: number; longitude: number; label?: string };

type Props = {
  title: string;
  value: PickedPoint | null;
  onChange: (point: PickedPoint | null) => void;
  gps: { latitude: number; longitude: number } | null;
  allowGps: boolean;
  usingGps: boolean;
  onUseGps: (on: boolean) => void;
  placeholder?: string;
};

/** Elige un punto de la ruta: ubicación actual (GPS), una dirección escrita o un pin en el mapa. */
export function PointPicker({ title, value, onChange, gps, allowGps, usingGps, onUseGps, placeholder }: Props) {
  const [method, setMethod] = useState<"gps" | "text" | "map">(allowGps && usingGps ? "gps" : "text");
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const frame = useRef<HTMLIFrameElement | null>(null);

  // El pin del mapa llega por postMessage; solo se acepta el que viene de NUESTRO iframe.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (!frame.current || event.source !== frame.current.contentWindow) return;
      let data: { type?: string; latitude?: number; longitude?: number } | null = null;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        data = null;
      }
      if (!data || data.type !== "hermes-pin") return;
      const latitude = Number(data.latitude);
      const longitude = Number(data.longitude);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
      onChange({ latitude, longitude, label: "Punto del mapa" });
      setNote("📌 Punto del mapa elegido.");
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [onChange]);

  const choose = (next: "gps" | "text" | "map") => {
    setMethod(next);
    onUseGps(next === "gps");
    if (next === "gps") {
      onChange(gps ? { latitude: gps.latitude, longitude: gps.longitude, label: "Mi ubicación" } : null);
      setNote(gps ? "Se usará tu ubicación actual." : "Esperando el GPS…");
    }
  };

  const search = async () => {
    if (text.trim().length < 3) {
      setNote("Escribe al menos la ciudad o la calle.");
      return;
    }
    setBusy(true);
    setNote("Buscando…");
    try {
      const hit = await geocodeAddress(text.trim());
      if (!hit) {
        setNote("No encontré ese sitio. Prueba con «ciudad, calle» o elige en el mapa.");
        return;
      }
      onChange({ latitude: hit.latitude, longitude: hit.longitude, label: text.trim() });
      setNote(`📌 ${text.trim()} situado.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <fieldset className="point-picker">
      <legend>{title}</legend>
      <div className="time-row">
        {allowGps ? (
          <button type="button" className={`time-chip${method === "gps" ? " on" : ""}`} onClick={() => choose("gps")}>
            📍 Mi ubicación
          </button>
        ) : null}
        <button type="button" className={`time-chip${method === "text" ? " on" : ""}`} onClick={() => choose("text")}>
          ✍️ Escribir lugar
        </button>
        <button type="button" className={`time-chip${method === "map" ? " on" : ""}`} onClick={() => choose("map")}>
          🗺️ Elegir en el mapa
        </button>
      </div>
      {method === "text" ? (
        <div className="point-text">
          <input
            type="text"
            value={text}
            placeholder={placeholder || "Ciudad o dirección (ej. Manresa)"}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void search();
              }
            }}
          />
          <button type="button" className="btn-copper" disabled={busy} onClick={() => void search()}>
            {busy ? "Buscando…" : "Situar"}
          </button>
        </div>
      ) : null}
      {method === "map" ? (
        <div className="map-frame point-map">
          <iframe
            ref={frame}
            title={title}
            sandbox="allow-scripts allow-same-origin"
            srcDoc={leafletPinPickerHtml(value?.latitude ?? gps?.latitude ?? 41.7, value?.longitude ?? gps?.longitude ?? 1.83)}
          />
        </div>
      ) : null}
      <p className={value ? "point-ok" : "muted"}>
        {note || (value ? `📌 ${value.label || `${value.latitude.toFixed(4)}, ${value.longitude.toFixed(4)}`}` : "Sin elegir todavía")}
      </p>
    </fieldset>
  );
}
